import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    LIVE_MAP_DEMO_UPDATE_INTERVAL_MS,
    buildLiveMapDemoFleet,
    getLiveMapDemoDrivers,
    getLiveMapDemoDrives,
} from './liveMapDemoSimulator';

/**
 * Starts and stops the in-browser live map demo. While it runs, `fleet` holds the fake
 * cars, stands and history, and `drivers` and `drives` advance every
 * LIVE_MAP_DEMO_UPDATE_INTERVAL_MS. Rides cancelled by the dispatcher only leave `drives`.
 */
export const useLiveMapDemo = ({ withBusy }) => {
    const [fleet, setFleet] = useState(null);
    const [tick, setTick] = useState(0);
    const [cancelledDriveIds, setCancelledDriveIds] = useState(() => new Set());
    // Bumped on every start/stop so a slow geography load cannot revive a stopped demo.
    const runIdRef = useRef(0);
    // Read when a run starts, so a later feature-flag update does not restart a running demo.
    const withBusyRef = useRef(withBusy);

    useEffect(() => {
        withBusyRef.current = withBusy;
    }, [withBusy]);

    const stop = useCallback(() => {
        runIdRef.current += 1;
        setFleet(null);
        setTick(0);
        setCancelledDriveIds(new Set());
    }, []);

    const start = useCallback(async () => {
        runIdRef.current += 1;
        const runId = runIdRef.current;

        try {
            // Separate chunk: the street geometry is only downloaded when a demo starts.
            const { default: geography } = await import('./liveMapDemoGeography.json');
            if (runId !== runIdRef.current) return;

            setFleet(
                buildLiveMapDemoFleet(geography, {
                    withBusy: withBusyRef.current,
                    startedAtMs: Date.now(),
                })
            );
            setCancelledDriveIds(new Set());
            // Tick 1 right away, so the cars start driving the moment the demo appears.
            setTick(1);
        } catch (error) {
            console.error('Live map demo could not start:', error);
            if (runId === runIdRef.current) stop();
        }
    }, [stop]);

    useEffect(() => {
        if (!fleet) return undefined;

        const intervalId = setInterval(
            () => setTick((current) => current + 1),
            LIVE_MAP_DEMO_UPDATE_INTERVAL_MS
        );
        return () => clearInterval(intervalId);
    }, [fleet]);

    const cancelDrive = useCallback((driveId) => {
        setCancelledDriveIds((ids) => new Set(ids).add(driveId));
    }, []);

    const drivers = useMemo(
        () => (fleet ? getLiveMapDemoDrivers(fleet, tick) : []),
        [fleet, tick]
    );

    const drives = useMemo(
        () =>
            fleet
                ? getLiveMapDemoDrives(fleet, tick * LIVE_MAP_DEMO_UPDATE_INTERVAL_MS).filter(
                      (drive) => !cancelledDriveIds.has(drive.drive_id)
                  )
                : [],
        [fleet, tick, cancelledDriveIds]
    );

    return { fleet, drivers, drives, start, stop, cancelDrive };
};
